import { KeyRound, Pencil, Users } from "lucide-react";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import inviteeAddStyles from "@/features/invitee/styles/inviteeAdd.module.css";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import {
  RELATIONSHIP_OPTIONS,
  canRemoveCoResident,
  relationshipLabel,
} from "../propertyTabHelpers";
import type {
  CoResidentFormRow,
  PropertyCoResidentEditSectionProps,
  PropertyLeasingAgentEditSectionProps,
} from "../../../types/tabs";

export function PropertyLeasingAgentEditSection({
  leasingConsultantLabel,
  loadingAgents,
  agentOptions,
  formData,
  propertyInlineMessage,
  onAgentSelection,
  onInputChange,
  onAgentPhoneInput,
}: PropertyLeasingAgentEditSectionProps) {
  return (
    <div className={styles.section} data-testid="application-property-tab-section-leasing-agent">
      <div className={styles.propertyEditNote}>
        If {leasingConsultantLabel} is unknown select the Other and in the name field type Unknown
      </div>

      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <KeyRound className="h-4 w-4" />
        </span>
        Leasing Agent
      </h2>
      {loadingAgents ? (
        <div className="text-sm text-slate-600">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={inviteeAddStyles.formGroup}>
            <label className={inviteeAddStyles.formLabel} htmlFor="agent_id" data-testid="application-property-tab-agent-id-label">
              {leasingConsultantLabel}
            </label>
            <SearchableSelect
              triggerDataTestId="application-property-tab-agent-id-input"
              options={agentOptions.map((opt) => ({
                ...opt,
                value: String(opt.value),
                label: String(opt.label),
              }))}
              value={formData.agent_id || ""}
              onChange={(v) => onAgentSelection(String(v))}
              placeholder={`Choose ${leasingConsultantLabel}...`}
              searchPlaceholder={`Search ${leasingConsultantLabel}`}
              searchable={false}
              className={inviteeAddStyles.customFormControl}
            />
          </div>

          {String(formData.agent_id) === "Other" ? (
            <div className={inviteeAddStyles.formGroup}>
              <label className={inviteeAddStyles.formLabel} htmlFor="agent_name" data-testid="application-property-tab-agent-name-label">
                {leasingConsultantLabel}/Property Manager Name
              </label>
              <input
                id="agent_name"
                type="text"
                className={`${inviteeAddStyles.customFormControl}${propertyInlineMessage("agent_name") ? ` ${editStyles.inputError}` : ""}`}
                value={formData.agent_name || ""}
                data-testid="application-property-tab-agent-name-input"
                onChange={(e) => onInputChange("agent_name", e.target.value)}
                placeholder={`${leasingConsultantLabel} Name`}
              />
              {propertyInlineMessage("agent_name") ? (
                <div className={editStyles.errorMessage} role="alert">
                  {propertyInlineMessage("agent_name")}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className={inviteeAddStyles.formGroup}>
            <label className={inviteeAddStyles.formLabel} htmlFor="agent_email" data-testid="application-property-tab-agent-email-label">
              {leasingConsultantLabel}/Property Manager Email
              {String(formData.agent_id) === "Other" ? (
                <span className={editStyles.required}>*</span>
              ) : null}
            </label>
            <input
              id="agent_email"
              type="text"
              className={`${inviteeAddStyles.customFormControl}${propertyInlineMessage("agent_email") ? ` ${editStyles.inputError}` : ""}`}
              value={formData.agent_email || ""}
              data-testid="application-property-tab-agent-email-input"
              onChange={(e) =>
                onInputChange(
                  "agent_email",
                  e.target.value.trim().toLowerCase(),
                )
              }
              placeholder={`${leasingConsultantLabel} Email`}
              readOnly={String(formData.agent_id) !== "Other"}
            />
            {propertyInlineMessage("agent_email") ? (
              <div className={editStyles.errorMessage} role="alert">
                {propertyInlineMessage("agent_email")}
              </div>
            ) : null}
          </div>

          <div className={inviteeAddStyles.formGroup}>
            <label className={inviteeAddStyles.formLabel} htmlFor="agent_mobile" data-testid="application-property-tab-agent-mobile-label">
              {leasingConsultantLabel}/Property Manager Phone
              {String(formData.agent_id) === "Other" ? (
                <span className={editStyles.required}>*</span>
              ) : null}
            </label>
            <input
              id="agent_mobile"
              type="text"
              className={`${inviteeAddStyles.customFormControl}${propertyInlineMessage("agent_phone") ? ` ${editStyles.inputError}` : ""}`}
              value={formData.agent_phone || ""}
              data-testid="application-property-tab-agent-mobile-input"
              onChange={(e) => onAgentPhoneInput(e.target.value)}
              placeholder="(XXX) XXX-XXXX"
              readOnly={String(formData.agent_id) !== "Other"}
            />
            {propertyInlineMessage("agent_phone") ? (
              <div className={editStyles.errorMessage} role="alert">
                {propertyInlineMessage("agent_phone")}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export function PropertyCoResidentEditSection({
  formData,
  resendInviteLoading,
  coResidentFieldMsg,
  onInputChange,
  onCoResidentChange,
  onCoResidentPhoneInput,
  onAddCoResident,
  onRemoveCoResident,
  onRequestResendInvite,
  isCoApplicantView,
  readOnlyCoResidents,
}: PropertyCoResidentEditSectionProps) {
  // Co-resident field groups are mounted inline on "Add Co-Resident"; re-run Google
  // Translate so Spanish applies to the freshly mounted fields (Angular keeps them in-DOM).
  useGoogleTranslateRefresh(formData.co_residents.length);

  return (
    <div className={styles.section} data-testid="application-property-tab-section-co-residents">
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <Users className="h-4 w-4" />
        </span>
        Co-Residents
      </h2>
      {isCoApplicantView ? (
        <CoResidentReadOnlyList coResidents={readOnlyCoResidents} />
      ) : (
      <>
      <div className={editStyles.formRow}>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid="application-property-tab-other-individuals-label">
            Will other individuals be on this lease? (Do not include
            children under the age of 18)
          </label>
          <div className="flex flex-wrap gap-4 mt-2">
            <button
              type="button"
              className={`px-6 py-2 border rounded-md transition-colors ${
                formData.other_individuals === true
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="application-property-tab-other-individuals-yes-button"
              onClick={() => onInputChange("other_individuals", true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`px-6 py-2 border rounded-md transition-colors ${
                formData.other_individuals === false
                  ? 'bg-[#1597ff] text-white border-[#1597ff]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="application-property-tab-other-individuals-no-button"
              onClick={() => onInputChange("other_individuals", false)}
            >
              No
            </button>
          </div>
        </div>
      </div>

      {formData.other_individuals === true ? (
        <div className="mt-6">
          <div className={styles.propertyEditNote}>
            List all adults that will be on this lease. Do not list any children under the age of 18 in the section below. All adult individuals on the lease will need to be listed in this section.
            Each individual is required to complete their own application and pay their own $35.00 application fee. We will send an email to each resident listed below with a link to their
            application.
          </div>

          {formData.co_residents.map((resident, index) => (
            <div key={index} className={styles.coResidentPanelWrap}>
              {canRemoveCoResident(resident) ? (
                <div className={styles.coResidentRemoveRow}>
                  <button
                    type="button"
                    className={styles.coResidentRemoveButton}
                    data-testid={`application-property-tab-co-resident-${index}-remove-button`}
                    onClick={() => onRemoveCoResident(index)}
                  >
                    <Pencil className={styles.coResidentRemoveIcon} aria-hidden="true" />
                    Remove
                  </button>
                </div>
              ) : null}
              <div className={styles.coResidentSubPanel}>
                <div
                  className={`${styles.coResidentSubPanelNamesRow}${
                    resident.invite_code
                      ? ` ${styles.coResidentSubPanelNamesRowWithInvite}`
                      : ""
                  }`}
                >
                  <CoResidentTextField
                    index={index}
                    field="first_name"
                    label="First Name"
                    required
                    value={resident.first_name}
                    message={coResidentFieldMsg(index, "first_name")}
                    onChange={onCoResidentChange}
                    labelClassName={styles.coResidentSubPanelLabel}
                    fieldClassName={styles.coResidentSubPanelField}
                  />
                  <CoResidentTextField
                    index={index}
                    field="middle_name"
                    label="Middle Name"
                    value={resident.middle_name}
                    message={undefined}
                    onChange={onCoResidentChange}
                    labelClassName={styles.coResidentSubPanelLabel}
                    fieldClassName={styles.coResidentSubPanelField}
                  />
                  <CoResidentTextField
                    index={index}
                    field="last_name"
                    label="Last Name"
                    required
                    value={resident.last_name}
                    message={coResidentFieldMsg(index, "last_name")}
                    onChange={onCoResidentChange}
                    labelClassName={styles.coResidentSubPanelLabel}
                    fieldClassName={styles.coResidentSubPanelField}
                  />
                  {resident.invite_code ? (
                    <div className={styles.coResidentSubPanelField}>
                      <label
                        className={styles.coResidentSubPanelLabel}
                        data-testid={`application-property-tab-co-resident-${index}-invite-code-label`}
                      >
                        Invite Code
                      </label>
                      <input
                        type="text"
                        className={styles.coResidentReadonlyInput}
                        value={resident.invite_code}
                        data-testid={`application-property-tab-co-resident-${index}-invite-code-input`}
                        placeholder="Invite Code"
                        readOnly
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-1 text-sm">
                        <span
                          className="text-[13px] font-normal leading-[1.35] text-[#1597ff]"
                          data-testid={`application-property-tab-co-resident-${index}-already-sent-label`}
                        >
                          Already Sent :
                        </span>
                        <button
                          type="button"
                          className="font-medium text-[#1597ff] underline underline-offset-2 hover:text-[#0f78cc] disabled:cursor-not-allowed disabled:opacity-60"
                          data-testid={`application-property-tab-co-resident-${index}-resend-button`}
                          disabled={resendInviteLoading}
                          onClick={() => onRequestResendInvite(String(resident.invite_code))}
                        >
                          Resend
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={styles.coResidentSubPanelContactRow}>
                  <div className={styles.coResidentSubPanelField}>
                    <label
                      className={styles.coResidentSubPanelLabel}
                      data-testid={`application-property-tab-co-resident-${index}-phone-label`}
                    >
                      Phone No
                      <span className={editStyles.required}>*</span>
                    </label>
                    <input
                      type="tel"
                      className={`${editStyles.input}${coResidentFieldMsg(index, "phone") ? ` ${editStyles.inputError}` : ""}`}
                      value={resident.phone}
                      data-testid={`application-property-tab-co-resident-${index}-phone-input`}
                      onChange={(e) => onCoResidentPhoneInput(index, e.target.value)}
                      placeholder="(XXX) XXX-XXXX"
                      autoComplete="tel"
                    />
                    {coResidentFieldMsg(index, "phone") ? (
                      <div className={editStyles.errorMessage} role="alert">
                        {coResidentFieldMsg(index, "phone")}
                      </div>
                    ) : null}
                  </div>
                  <span className={styles.coResidentSubPanelOr}>OR</span>
                  <div className={styles.coResidentSubPanelField}>
                    <label
                      className={styles.coResidentSubPanelLabel}
                      data-testid={`application-property-tab-co-resident-${index}-email-label`}
                    >
                      Email(optional)
                    </label>
                    <input
                      type="email"
                      className={`${editStyles.input}${coResidentFieldMsg(index, "email") ? ` ${editStyles.inputError}` : ""}`}
                      value={resident.email}
                      data-testid={`application-property-tab-co-resident-${index}-email-input`}
                      onChange={(e) =>
                        onCoResidentChange(
                          index,
                          "email",
                          e.target.value.trim().toLowerCase(),
                        )
                      }
                      placeholder="Email"
                      autoComplete="email"
                    />
                    {coResidentFieldMsg(index, "email") ? (
                      <div className={editStyles.errorMessage} role="alert">
                        {coResidentFieldMsg(index, "email")}
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.coResidentSubPanelField}>
                    <label
                      className={styles.coResidentSubPanelLabel}
                      data-testid={`application-property-tab-co-resident-${index}-relationship-label`}
                    >
                      Relationship
                      <span className={editStyles.required}>*</span>
                    </label>
                    <SearchableSelect
                      triggerDataTestId={`application-property-tab-co-resident-${index}-relationship-select`}
                      options={RELATIONSHIP_OPTIONS.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                      }))}
                      value={resident.relationship || ""}
                      onChange={(value) => onCoResidentChange(index, "relationship", String(value))}
                      placeholder="Select"
                      searchPlaceholder="Search Relationship"
                      searchable={false}
                      className={`${styles.propertyCustomSelect}${coResidentFieldMsg(index, "relationship") ? ` ${editStyles.inputError}` : ""}`}
                    />
                    {coResidentFieldMsg(index, "relationship") ? (
                      <div className={editStyles.errorMessage} role="alert">
                        {coResidentFieldMsg(index, "relationship")}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className={styles.propertyEditNote}>
            NOTE: If you notice any of the above information is incorrect, please contact your leasing agent to fix your info and resend the invite code.
          </div>

          <button 
             type="button" 
             data-testid="application-property-tab-add-co-resident-button"
             onClick={onAddCoResident}
             className="flex items-center text-blue-500 hover:text-blue-600 font-medium"
          >
             <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
             Add Co-Residents
          </button>
        </div>
      ) : null}
      </>
      )}
    </div>
  );
}

function CoResidentReadOnlyList({
  coResidents,
}: {
  coResidents: CoResidentFormRow[];
}) {
  if (!coResidents.length) return null;
  return (
    <div className="mt-6" data-testid="application-property-tab-co-residents-readonly">
      {coResidents.map((resident, index) => (
        <div key={index} className={styles.coResidentPanelWrap}>
          <div className={styles.coResidentSubPanel}>
            <div
              className={`${styles.coResidentSubPanelNamesRow}${
                resident.invite_code
                  ? ` ${styles.coResidentSubPanelNamesRowWithInvite}`
                  : ""
              }`}
            >
              <ReadOnlyCoResidentField index={index} field="first-name" label="First Name" value={resident.first_name} />
              <ReadOnlyCoResidentField index={index} field="middle-name" label="Middle Name" value={resident.middle_name} />
              <ReadOnlyCoResidentField index={index} field="last-name" label="Last Name" value={resident.last_name} />
              {resident.invite_code ? (
                <div className={styles.coResidentSubPanelField}>
                  <label
                    className={styles.coResidentSubPanelLabel}
                    data-testid={`application-property-tab-readonly-co-resident-${index}-invite-code-label`}
                  >
                    Invite Code
                  </label>
                  <input
                    type="text"
                    className={styles.coResidentReadonlyInput}
                    value={resident.invite_code}
                    data-testid={`application-property-tab-readonly-co-resident-${index}-invite-code-input`}
                    placeholder="Invite Code"
                    readOnly
                  />
                  <div className="mt-2 text-sm">
                    <span className={styles.coResidentSubPanelLabel}>Already Sent</span>
                  </div>
                </div>
              ) : null}
            </div>
            <div className={styles.coResidentSubPanelContactRow}>
              <ReadOnlyCoResidentField index={index} field="phone" label="Phone No" value={resident.phone} />
              <span className={styles.coResidentSubPanelOr}>OR</span>
              <ReadOnlyCoResidentField index={index} field="email" label="Email(optional)" value={resident.email} />
              <ReadOnlyCoResidentField
                index={index}
                field="relationship"
                label="Relationship"
                value={resident.relationship ? relationshipLabel(resident.relationship) : ""}
              />
            </div>
          </div>
        </div>
      ))}
      <div className={styles.propertyEditNote}>
        NOTE: If you notice any of the above information is incorrect, please contact your leasing agent to fix your info and resend the invite code.
      </div>
    </div>
  );
}

function ReadOnlyCoResidentField({
  index,
  field,
  label,
  value,
}: {
  index: number;
  field: string;
  label: string;
  value: string;
}) {
  return (
    <div className={styles.coResidentSubPanelField}>
      <label
        className={styles.coResidentSubPanelLabel}
        data-testid={`application-property-tab-readonly-co-resident-${index}-${field}-label`}
      >
        {label}
      </label>
      <input
        type="text"
        className={styles.coResidentReadonlyInput}
        value={value}
        data-testid={`application-property-tab-readonly-co-resident-${index}-${field}-input`}
        placeholder={label}
        readOnly
      />
    </div>
  );
}

function CoResidentTextField({
  index,
  field,
  label,
  required = false,
  value,
  message,
  onChange,
  labelClassName,
  fieldClassName,
}: {
  index: number;
  field: string;
  label: string;
  required?: boolean;
  value: string;
  message: string | undefined;
  onChange: (index: number, field: string, value: string | boolean) => void;
  labelClassName?: string;
  fieldClassName?: string;
}) {
  const testField = field.replace(/_/g, "-");
  return (
    <div className={fieldClassName ?? `${editStyles.formGroup} ${editStyles.coResidentFormGroup}`}>
      <label
        className={labelClassName ?? editStyles.label}
        data-testid={`application-property-tab-co-resident-${index}-${testField}-label`}
      >
        {label}
        {required ? <span className={editStyles.required}>*</span> : null}
      </label>
      <input
        type="text"
        className={`${editStyles.input}${message ? ` ${editStyles.inputError}` : ""}`}
        value={value}
        data-testid={`application-property-tab-co-resident-${index}-${testField}-input`}
        onChange={(e) => onChange(index, field, e.target.value)}
        placeholder={label}
      />
      {message ? (
        <div className={editStyles.errorMessage} role="alert">
          {message}
        </div>
      ) : null}
    </div>
  );
}
